import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import IconShopMore from '../../../assets/svg/IconShopMore';
import ListItem from '../../../components/ListItem';
import { AnalyticsEvent } from '../../../constants/AnalyticsEvent';
import { checkItemViewed } from '../../../context/devLogFirehose';
import { translate } from '../../../i18n';
import { OnboardingPayload } from '../../../redux/actions/getOnboardingActions';
import { ItemTypes, ListLoadingTypes } from '../../../types/Common';
import { MarketingLandingPageItem } from '../../../types/MarketingLandingPageResponse';
import ItemSources from '../../../utils/ItemSources';
import { navigate } from '../../../utils/navigationService';
import routeUtils from '../../../utils/routes';
import FlatListWithImpression, {
  FlatListWithImpressionType,
} from '../../FlatListWithImpression';
import { itemProducer } from '../../FlatListWithImpression/utils';
import OnboardingImage from './OnboardingImage';
import styles, { ITEM_MARGIN, ITEM_WIDTH, S } from './styles';

export interface OnboardingRefMethods {
  setIsVisible: (isVisible: boolean) => void;
}
export interface Props {
  onBoardingIdList: string[];
  isFetchingWholeOnboarding: boolean;
  loadingType: ListLoadingTypes;
  isNewFetching: boolean;
  isHomePageReload: boolean;
  logEvent: Function;
  isFocused: boolean;
  getOnboardingList: (payload: OnboardingPayload) => void;
  isNewCustomer: boolean;
}

const keyExtractor = item => `${item}`;
const layoutName = 'index_newuser';
const sources = new ItemSources<MarketingLandingPageItem>({
  productKey: product => `${product.id}`,
  productSource: product => product.source || ItemTypes.USER_SUBMIT,
});
const makeLogEventPayload = (args = {}) => ({
  logEvent: AnalyticsEvent.GeneralEvent,
  data: {
    action_name: 'view_others',
    event_category: 'observation metric',
    non_interaction: true,
    ...args,
  },
});

const footerLogData = [
  {
    actionName: 'click_others',
    eventCategory: 'observation metric',
    eventLabel: 'index_newuserWatchmoreBtn',
    nonInteraction: false,
  },
];

export default forwardRef((props: Props, ref) => {
  const {
    onBoardingIdList,
    isNewFetching,
    isFetchingWholeOnboarding,
    isHomePageReload,
    logEvent,
    isFocused,
    getOnboardingList,
    isNewCustomer,
  } = props;
  const [isVisible, setIsVisible] = useState(false);
  const impressionList = useRef<FlatListWithImpressionType>();
  const haveSeenGift = useRef(false);

  useImperativeHandle(ref, () => ({
    setIsVisible,
  }));

  const getList = useCallback(
    (args = {}) => {
      getOnboardingList({
        page: 1,
        loadingType: 'new',
        success: sources.processSource,
        ...args,
      });
    },
    [getOnboardingList]
  );

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    const onRefresh = () => {
      const impression = impressionList.current;

      if (typeof impression?.clearImpressionAccumulatedItems === 'function') {
        impression.clearImpressionAccumulatedItems();
      }

      getList({ loadingType: 'reload' });
    };

    if (isHomePageReload) {
      onRefresh();
    }
  }, [getList, isHomePageReload]);

  useEffect(() => {
    if (isVisible && !haveSeenGift.current) {
      haveSeenGift.current = true;
      logEvent(makeLogEventPayload({ event_label: 'index_newuser' }));
    }
  }, [isVisible, logEvent]);

  const onPressMore = () => {
    navigate('OnboardingPage');
  };

  const renderItem = ({ item, index }: { item: string; index: number }) => {
    return (
      <ListItem
        index={index}
        key={item}
        id={item}
        // PM: 橫向列表不顯示 ribbon、劃掉的價格
        showRibbon={false}
        showLineThroughPrice={false}
        numColumns={1}
        showLoading={`${item}`.includes('blank')}
        itemWidth={ITEM_WIDTH}
        itemMargin={ITEM_MARGIN}
        contentWrapperStyle={styles.contentWrapperStyle}
        titleStyle={styles.listItemFontStyle}
        priceCurrencyStyle={styles.listItemFontStyle}
        priceTildeStyle={styles.listItemFontStyle}
        priceStyle={styles.listItemPriceStyle}
        wrapperStyle={
          isNewFetching
            ? { borderWidth: 0, marginTop: 0 }
            : styles.listItemWrapperStyle
        }
        layoutName={layoutName}
        source={sources.get(item)}
        showTitlesAndPrice={false}
        shouldDisplayImpressionForDebug={checkItemViewed(
          impressionList?.current?.accumulatedItems,
          item
        )}
        isShowItemLabel={false}
      />
    );
  };

  const renderListFooter = () => {
    return (
      <S.ListFooter
        accessibilityRole="link"
        logData={footerLogData}
        href={routeUtils.compileToPath('OnboardingPage')}
        onPress={onPressMore}
        testID="WatchMoreLink"
      >
        <S.ListFooterTextContainer>
          <IconShopMore />
          <S.ListFooterText>{translate('watch-more')}</S.ListFooterText>
        </S.ListFooterTextContainer>
      </S.ListFooter>
    );
  };

  const ListFooterLogEvent = () => {
    if (isFocused) {
      logEvent(
        makeLogEventPayload({ event_label: 'index_newuserWatchmoreBtn' })
      );
    }
  };

  if (!onBoardingIdList.length || !isNewCustomer) {
    return null;
  }

  return (
    <S.ListContainer isNewFetching={isNewFetching}>
      <FlatListWithImpression
        horizontal
        testID="OnboardingList"
        ref={impressionList}
        ListHeaderComponent={
          <OnboardingImage
            showLoading={isNewFetching}
            testID="OnboardingListItemImage"
          />
        }
        ListFooterComponent={renderListFooter}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        dataProducer={itemProducer}
        data={onBoardingIdList}
        onEndReached={ListFooterLogEvent}
        onEndReachedThreshold={0.1}
        showsHorizontalScrollIndicator={false}
        // 處理 loading Onboarding 時會被當作 visible 的問題，
        // 這邊會等待 Onboarding 兩支 API 確定 fetching 結束後，才可以被視為 visible
        isVisible={isVisible && !isFetchingWholeOnboarding}
        layoutName={layoutName}
        sources={sources}
      />
    </S.ListContainer>
  );
});
